
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ArticlePreview from '@/components/dashboard/editor/ArticlePreview';

interface PreviewArticleData {
  title: string;
  lead?: string;
  content: string;
  headerImageUrl?: string;
}

const ArticlePreviewPage: React.FC = () => {
  const [previewData, setPreviewData] = useState<PreviewArticleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { previewKey } = useParams<{ previewKey: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    // Retrieve the article data from sessionStorage
    if (previewKey) {
      const storageKey = `article_preview_${previewKey}`;
      console.log('Looking for preview data with key:', storageKey);
      
      const storedData = sessionStorage.getItem(storageKey);
      console.log('Retrieved data from sessionStorage:', storedData);
      
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          console.log('Parsed preview data:', parsedData);
          setPreviewData(parsedData);
        } catch (error) {
          console.error('Error parsing preview data:', error);
        }
      } else {
        console.error('No preview data found for key:', storageKey);
      }
    }
    setIsLoading(false);
  }, [previewKey]);

  const handleReturnToEditor = () => {
    window.close(); // Close the preview tab
    // If the window doesn't close (popup blockers), navigate back
    setTimeout(() => {
      if (window.opener === null) {
        navigate('/dashboard');
      }
    }, 300);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <h1 className="text-2xl font-bold text-destructive mb-2">Preview Unavailable</h1>
        <p className="text-muted-foreground text-center mb-6">
          The preview data could not be found or has expired. Please return to the editor and try again.
        </p>
        <Button variant="outline" onClick={handleReturnToEditor}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Return to Editor
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center">
          <Button variant="outline" onClick={handleReturnToEditor} size="sm">
            <ArrowLeft className="mr-2 h-4 w-4" /> Return to Editor
          </Button>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <h1 className="text-3xl font-bold mb-2">Article Preview</h1>
            <div className="text-sm text-muted-foreground mb-6">
              This is a preview of how your article will appear when published.
            </div>
            <div className="border rounded-lg overflow-hidden">
              <ArticlePreview
                title={previewData.title}
                content={previewData.content}
                lead={previewData.lead}
                headerImageUrl={previewData.headerImageUrl}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticlePreviewPage;
